/**
 * How an address will print for vendor bookings (Worldfirst / ICL / Atlantic):
 * two lines of at most 30 characters (vendor API Document; Worldfirst cuts
 * anything longer on the label). Mirrors `wrapAddress` in the backend
 * (BE src/vendors/xpresion/mapParcel.js) so the preview matches what is sent.
 *
 *  - Staff's own lines are kept when they already fit.
 *  - Otherwise the address is re-wrapped at spaces, never mid-word.
 *  - `overflow` = it needs more than two lines; the booking will be refused.
 */
export const ADDRESS_LINE_MAX = 30;
export const ADDRESS_MAX_LINES = 2;

const clean = (s) =>
    String(s || "")
        .replace(/\s+/g, " ")
        .replace(/\s+,/g, ",")
        .replace(/,{2,}/g, ",")
        .trim()
        .replace(/^,+|,+$/g, "")
        .trim();

export function wrapAddress(parts, max = ADDRESS_LINE_MAX) {
    const cleaned = (parts || []).map(clean).filter(Boolean);
    if (cleaned.length <= ADDRESS_MAX_LINES && cleaned.every((p) => p.length <= max)) {
        return { lines: cleaned, overflow: false };
    }
    const out = [];
    let cur = "";
    for (let word of cleaned.join(", ").split(" ")) {
        while (word.length > max) {
            if (cur) {
                out.push(cur);
                cur = "";
            }
            out.push(word.slice(0, max));
            word = word.slice(max);
        }
        if (!word) continue;
        if (!cur) cur = word;
        else if (`${cur} ${word}`.length <= max) cur = `${cur} ${word}`;
        else {
            out.push(cur);
            cur = word;
        }
    }
    if (cur) out.push(cur);
    return { lines: out, overflow: out.length > ADDRESS_MAX_LINES };
}

/** Helper text for the Address 2 field. */
export function vendorAddressHint(fit) {
    if (!fit.lines.length) return "";
    if (fit.overflow) {
        return `Too long for Worldfirst / ICL / Atlantic: needs ${fit.lines.length} lines of ${ADDRESS_LINE_MAX} (max ${ADDRESS_MAX_LINES}). Shorten it.`;
    }
    return `Vendor label: ${fit.lines.map((l) => `"${l}"`).join(" / ")}`;
}
